import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FaClipboardList, FaUser, FaCheckCircle, FaExclamationCircle, FaClock } from 'react-icons/fa';
import axios from 'axios';
import './GuestAssessment.css';

// Create a simple axios instance for guest requests (no auth required)
// ALWAYS use direct backend URL - never go through SWA
const guestApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 
    (process.env.NODE_ENV === 'production' 
      ? 'https://changemanagement-backend-ahfre3hvgcerbrgp.canadacentral-01.azurewebsites.net/api'  // Direct Azure backend
      : 'http://localhost:5000/api' // Use localhost in development
    ),
  timeout: 30000, // Add 30 second timeout
  headers: {
    'Content-Type': 'application/json'
  }
});

console.log('🔗 Guest API Client configured for:', guestApi.defaults.baseURL);

// Enhanced request interceptor for debugging guest assessment issues
guestApi.interceptors.request.use(request => {
  console.log('🚀 Guest API Request:', {
    url: request.url,
    baseURL: request.baseURL,
    fullURL: `${request.baseURL}${request.url}`,
    method: request.method?.toUpperCase(),
    timestamp: new Date().toISOString()
  });
  return request;
});

// Enhanced response interceptor for debugging guest assessment issues
guestApi.interceptors.response.use(
  response => {
    console.log('✅ Guest API Success:', {
      url: response.config?.url,
      status: response.status,
      contentType: response.headers['content-type'],
      timestamp: new Date().toISOString()
    });
    return response;
  },
  error => {
    const errorDetails = {
      url: error.config?.url,
      fullURL: error.config ? `${error.config.baseURL}${error.config.url}` : 'Unknown',
      method: error.config?.method?.toUpperCase(),
      status: error.response?.status,
      statusText: error.response?.statusText,
      contentType: error.response?.headers['content-type'],
      responseData: error.response?.data,
      timestamp: new Date().toISOString()
    };
    
    console.log('❌ Guest API Error:', errorDetails);
    
    // Enhanced SWA routing detection
    if (error.response?.headers['content-type']?.includes('text/html')) {
      console.error('🚨 SWA ROUTING ISSUE DETECTED IN GUEST ASSESSMENT!');
      console.error('📋 Guest assessment is receiving HTML instead of JSON from backend');
    }
    
    return Promise.reject(error);
  }
);

const GuestAssessment = () => {
  const { invitationToken } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [assessmentData, setAssessmentData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);



  useEffect(() => {
    if (!invitationToken) {
      setError('Invalid invitation link');
      setLoading(false);
      return;
    }

    // Access the invitation and create guest session
    guestApi.get(`/guest/access/${invitationToken}`)
      .then(response => {
        setSessionData(response.data);
        return guestApi.get(`/guest/assessment/${response.data.session_token}`);
      })
      .then(response => {
        setAssessmentData(response.data);
        // Use the questions from the API response (now includes template questions)
        const apiQuestions = response.data.questions || [];
        if (apiQuestions.length > 0) {
          setQuestions(apiQuestions);
        } else {
          // Fallback to sample questions if no template questions available
          console.warn('No template questions found, using sample questions');
          setQuestions(getSampleQuestions());
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to access assessment:', err);
        if (err.response?.status === 410) {
          // Check if it's already completed
          if (err.response?.data?.completed) {
            setError('This assessment has already been completed. Thank you for your participation.');
            setSubmitted(true);
          } else {
            setError('This invitation has expired. Please contact the person who sent you this link for a new invitation.');
          }
        } else if (err.response?.status === 404) {
          setError('Invalid invitation link. Please check the link and try again.');
        } else {
          setError(err.response?.data?.error || 'Failed to load assessment. Please try again.');
        }
        setLoading(false);
      });
  }, [invitationToken]);

  // Sample questions for demonstration
  const getSampleQuestions = () => [
    {
      id: 1,
      text: "How would you rate your understanding of the upcoming changes?",
      type: "likert",
      required: true
    },
    {
      id: 2,
      text: "What concerns do you have about the changes?",
      type: "long_text",
      required: false
    },
    {
      id: 3,
      text: "How confident are you in your ability to adapt to the changes?",
      type: "likert",
      required: true
    },
    {
      id: 4,
      text: "What support would be most helpful for you during this transition?",
      type: "long_text",
      required: false
    },
    {
      id: 5,
      text: "How would you describe your overall attitude toward these changes?",
      type: "single_select",
      options: ["Very positive", "Positive", "Neutral", "Negative", "Very negative"],
      required: true
    }
  ];

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const validateAnswers = () => {
    const requiredQuestions = questions.filter(q => q.required);
    const missingAnswers = requiredQuestions.filter(q => {
      const answer = answers[q.id];
      if (!answer) return true;
      
      // Handle different answer types
      if (Array.isArray(answer)) {
        return answer.length === 0; // Multi-select with no selections
      }
      
      if (typeof answer === 'string') {
        return answer.trim() === ''; // Empty string
      }
      
      return false; // Has some value
    });
    return missingAnswers.length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateAnswers()) {
      setError('Please answer all required questions before submitting.');
      return;
    }

    setSubmitting(true);
    setError(null);

    console.log('🚀 Guest Assessment Submission:', {
      sessionToken: sessionData.session_token,
      answers: answers,
      participantName: sessionData?.participant_name,
      questionsCount: questions.length,
      answersCount: Object.keys(answers).length
    });

    try {
      const response = await guestApi.post(`/guest/assessment/${sessionData.session_token}/submit`, {
        answers: answers
      });
      console.log('✅ Guest Assessment Submitted Successfully:', response.data);
      setSubmitted(true);
    } catch (err) {
      console.error('❌ Failed to submit assessment:', err);
      console.error('Error details:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        config: err.config
      });
      
      if (err.response?.status === 410) {
        setError('Your session has expired. Please use the original invitation link to start a new session.');
      } else if (err.response?.status === 400 && err.response?.data?.error?.includes('already been submitted')) {
        setError('This assessment has already been submitted. Thank you for your participation.');
        setSubmitted(true);
      } else {
        setError(err.response?.data?.error || 'Failed to submit assessment. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestion = (question) => {
    const value = answers[question.id] || '';

    switch (question.type) {
      case 'scale':
        // 1-5 rating scale
        return (
          <div className="likert-scale">
            {[1, 2, 3, 4, 5].map(rating => (
              <label key={rating} className="likert-option">
                <input
                  type="radio"
                  name={`question_${question.id}`}
                  value={rating}
                  checked={value === rating.toString()}
                  onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                />
                <span className="likert-label">{rating}</span>
              </label>
            ))}
          </div>
        );

      case 'likert':
        // Strongly Disagree to Strongly Agree
        return (
          <div className="likert-scale">
            {[1, 2, 3, 4, 5].map(rating => (
              <label key={rating} className="likert-option">
                <input
                  type="radio"
                  name={`question_${question.id}`}
                  value={rating}
                  checked={value === rating.toString()}
                  onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                />
                <span className="likert-label">{rating}</span>
                <span className="likert-text">
                  {rating === 1 ? 'Strongly Disagree' : 
                   rating === 2 ? 'Disagree' : 
                   rating === 3 ? 'Neutral' : 
                   rating === 4 ? 'Agree' : 'Strongly Agree'}
                </span>
              </label>
            ))}
          </div>
        );

      case 'comfort_scale':
        // Very Uncomfortable to Very Comfortable
        return (
          <div className="likert-scale">
            {[1, 2, 3, 4, 5].map(rating => (
              <label key={rating} className="likert-option">
                <input
                  type="radio"
                  name={`question_${question.id}`}
                  value={rating}
                  checked={value === rating.toString()}
                  onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                />
                <span className="likert-label">{rating}</span>
                <span className="likert-text">
                  {rating === 1 ? 'Very Uncomfortable' : 
                   rating === 2 ? 'Uncomfortable' : 
                   rating === 3 ? 'Neutral' : 
                   rating === 4 ? 'Comfortable' : 'Very Comfortable'}
                </span>
              </label>
            ))}
          </div>
        );

      case 'confidence_scale':
        // Not Confident to Very Confident
        return (
          <div className="likert-scale">
            {[1, 2, 3, 4, 5].map(rating => (
              <label key={rating} className="likert-option">
                <input
                  type="radio"
                  name={`question_${question.id}`}
                  value={rating}
                  checked={value === rating.toString()}
                  onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                />
                <span className="likert-label">{rating}</span>
                <span className="likert-text">
                  {rating === 1 ? 'Not Confident' : 
                   rating === 2 ? 'Slightly Confident' : 
                   rating === 3 ? 'Moderately Confident' : 
                   rating === 4 ? 'Confident' : 'Very Confident'}
                </span>
              </label>
            ))}
          </div>
        );

      case 'frequency_scale':
        // Never to Always
        return (
          <div className="likert-scale">
            {[1, 2, 3, 4, 5].map(rating => (
              <label key={rating} className="likert-option">
                <input
                  type="radio"
                  name={`question_${question.id}`}
                  value={rating}
                  checked={value === rating.toString()}
                  onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                />
                <span className="likert-label">{rating}</span>
                <span className="likert-text">
                  {rating === 1 ? 'Never' : 
                   rating === 2 ? 'Rarely' : 
                   rating === 3 ? 'Sometimes' : 
                   rating === 4 ? 'Often' : 'Always'}
                </span>
              </label>
            ))}
          </div>
        );

      case 'yes_no':
        return (
          <div className="select-options">
            {['Yes', 'No'].map((option, index) => (
              <label key={index} className="select-option">
                <input
                  type="radio"
                  name={`question_${question.id}`}
                  value={option}
                  checked={value === option}
                  onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        );

      case 'single_select':
        return (
          <div className="select-options">
            {(question.options || []).map((option, index) => (
              <label key={index} className="select-option">
                <input
                  type="radio"
                  name={`question_${question.id}`}
                  value={option}
                  checked={value === option}
                  onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        );

      case 'multi_select':
        const selectedValues = Array.isArray(value) ? value : (value ? [value] : []);
        return (
          <div className="select-options">
            {(question.options || []).map((option, index) => (
              <label key={index} className="select-option">
                <input
                  type="checkbox"
                  name={`question_${question.id}`}
                  value={option}
                  checked={selectedValues.includes(option)}
                  onChange={(e) => {
                    const newValues = selectedValues.includes(option)
                      ? selectedValues.filter(v => v !== option)
                      : [...selectedValues, option];
                    handleAnswerChange(question.id, newValues);
                  }}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        );

      case 'long_text':
        return (
          <textarea
            className="text-input long-text"
            value={value}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            placeholder={question.placeholder || "Please share your thoughts..."}
            rows={4}
          />
        );

      case 'short_text':
        return (
          <input
            type="text"
            className="text-input"
            value={value}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            placeholder={question.placeholder || "Your answer..."}
          />
        );

      default:
        // Fallback for unknown types
        console.warn(`Unknown question type: ${question.type}`);
        return (
          <input
            type="text"
            className="text-input"
            value={value}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            placeholder={question.placeholder || "Your answer..."}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="guest-assessment-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading assessment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="guest-assessment-page">
        <div className="error-container">
          <FaExclamationCircle className="error-icon" />
          <h2>Unable to Load Assessment</h2>
          <p>{error}</p>
          <button 
            className="retry-button"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="guest-assessment-page">
        <div className="success-container">
          <FaCheckCircle className="success-icon" />
          <h2>Assessment Completed Successfully!</h2>
          <p>Thank you for taking the time to complete this assessment.</p>
          <p>Your responses have been recorded and will be reviewed by the change management team.</p>
          <div className="participant-info">
            <p><strong>Participant:</strong> {sessionData?.participant_name}</p>
            <p><strong>Assessment:</strong> {assessmentData?.assessment.type}</p>
          </div>
          <p className="completion-note">
            You can now close this window. If you have any questions, please contact the person who sent you this assessment invitation.
          </p>
          <button 
            className="return-home-button"
            onClick={() => window.location.href = 'https://www.ezocm.com'}
            style={{
              marginTop: '30px',
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Visit ezOCM Website
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="guest-assessment-page">
      <div className="assessment-container">
        <header className="assessment-header">
          <div className="header-content">
            <FaClipboardList className="header-icon" />
            <div className="header-text">
              <h1>{assessmentData?.assessment.type}</h1>
              <p className="project-name">Project: {assessmentData?.assessment.project_name}</p>
            </div>
          </div>
          <div className="participant-info">
            <div className="participant-detail">
              <FaUser className="info-icon" />
              <span>{sessionData?.participant_name}</span>
            </div>
            <div className="session-info">
              <FaClock className="info-icon" />
              <span>Session expires at {assessmentData?.session?.expires_at ? new Date(assessmentData.session.expires_at).toLocaleTimeString() : 'N/A'}</span>
            </div>
          </div>
        </header>

        <div className="assessment-content">
          <div className="instructions">
            <h3>Instructions</h3>
            <p>
              Please take your time to thoughtfully answer each question. Your honest feedback 
              is valuable for improving the change management process. Questions marked with an 
              asterisk (*) are required.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="assessment-form">
            {questions.map((question, index) => (
              <div key={question.id} className="question-container">
                <div className="question-header">
                  <span className="question-number">{index + 1}</span>
                  <h4 className="question-text">
                    {question.text}
                    {question.required && <span className="required-mark">*</span>}
                  </h4>
                </div>
                <div className="question-input">
                  {renderQuestion(question)}
                </div>
              </div>
            ))}

            {error && (
              <div className="form-error">
                <FaExclamationCircle />
                <span>{error}</span>
              </div>
            )}

            <div className="form-actions">
              <button 
                type="submit" 
                className="submit-button"
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Assessment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default GuestAssessment; 